import Tooltip from 'tooltip.js'

// Los ⓘ operables (los que llevan `tabindex`) que hay en la página: Esc cierra el globo de cualquiera
// que lo tenga abierto, aunque no tenga el foco (lo abrió el ratón)
const operables = new Set()

function cerrarConEscape (evento) {
  if (evento.key === 'Escape') operables.forEach(globo => globo.cerrar())
}

// Pulsar fuera cierra los globos abiertos. tooltip.js ya lo hace con `mousedown`, pero Safari de iPhone no
// lanza ese evento al tocar un hueco sin nada pulsable; `pointerdown` sí llega siempre.
function cerrarAlPulsarFuera (evento) {
  if (evento.target.closest('emergia-tooltip-box')) return

  operables.forEach(globo => { if (!globo.contains(evento.target)) globo.cerrar() })
}

class EmergiaTooltip extends HTMLElement {
  connectedCallback () {
    this.tooltip = new Tooltip(this, {
      template: `
        <emergia-tooltip-box class="tooltip ${this.boxClass}" role="tooltip">
          <emergia-tooltip-arrow class="tooltip-arrow"></emergia-tooltip-arrow>
          <emergia-tooltip-inner class="tooltip-inner"></emergia-tooltip-inner>
        </emergia-tooltip-box>
      `,
      container: document.body,
      trigger: this._triggerMode(),
      closeOnClickOutside: true,
      placement: this.placement,
      popperOptions: {
        modifiers: {
          flip: {
            behavior: this._getFlipOptions(this.placement)
          },
          preventOverflow: {
            boundariesElement: 'viewport'
          }
        }
      },
      title: this.contenido,
      html: true
    })

    if (this.operable) this._activarTeclado()
  }

  disconnectedCallback () {
    if (!this.operable) return

    operables.delete(this)
    // Quita el globo que colgaba de `body` (sin esto quedaría huérfano al cambiar las tarjetas por otras). Si
    // el globo ya no está en la página, `dispose()` de tooltip.js revienta al intentar sacarlo.
    const caja = this.tooltip._tooltipNode
    if (!caja || caja.parentNode) this.tooltip.dispose()
  }

  // Un ⓘ con `tabindex` (`TarifasLuz::AyudaComponent`) es un control, no un adorno: se llega a él con Tab,
  // se abre con Intro o Espacio y se cierra con Esc. Sin `tabindex` el elemento se comporta como siempre.
  get operable() {
    return this.hasAttribute('tabindex')
  }

  // Abre el globo como lo haría el ratón: tooltip.js lo da por «abriéndose», que es lo que permite que
  // pulsar fuera lo cierre
  abrir () {
    this.tooltip._isOpening = true
    this.tooltip.show()
  }

  // `hide()` a secas deja a tooltip.js creyendo que sigue abriéndose y ya no volvería a abrir con el ratón
  cerrar () {
    this.tooltip._isOpening = false
    this.tooltip.hide()
  }

  _activarTeclado () {
    this.addEventListener('keydown', evento => {
      if (evento.key !== 'Enter' && evento.key !== ' ') return

      evento.preventDefault()
      this.tooltip._isOpen ? this.cerrar() : this.abrir()
    })

    document.addEventListener('keydown', cerrarConEscape)
    document.addEventListener('pointerdown', cerrarAlPulsarFuera)
    operables.add(this)
  }

  // Acepta tanto `top` como los modificadores de Popper `top-start` / `top-end`, que alinean la
  // caja con un borde del disparador en vez de centrarla. Hace falta cuando el disparador vive
  // pegado al borde de un contenedor estrecho: centrada, la caja se saldría y `preventOverflow` la
  // empujaría de vuelta dejando la flecha sin apuntar a nada. Lo que se valida es la base, así que
  // un valor desconocido sigue cayendo en `top` como antes.
  get placement() {
    const pedido = this.hasAttribute('placement') && this.getAttribute('placement')
    const [base] = `${pedido}`.split('-')

    return this._getFlipOptions().includes(base) ? `${pedido}` : 'top'
  }

  get message() {
    return this.hasAttribute('message') && this.getAttribute('message')
  }

  // Clase extra para la caja del globo, opcional. Existe porque la caja se cuelga de `body`
  // (`container: document.body`), fuera del contenedor de la página: sin una clase propia, darle
  // el aspecto de una maqueta concreta obligaría a redefinir `emergia-tooltip-box` en global y
  // repintaría todos los tooltips del sitio. Sin el atributo el resultado es el de siempre.
  get boxClass() {
    return this.getAttribute('box-class') || ''
  }

  // Lo que se pinta dentro del globo: el icono opcional y luego el mensaje.
  //
  // El texto va envuelto en un `<span>` para que sea un elemento y no un nodo de texto suelto:
  // así el `gap` del contenedor flex separa icono y texto de forma predecible.
  get contenido() {
    return `${this.boxIcon}<span>${this.message}</span>`
  }

  // Icono del globo, opcional. Viene de un `<template>` hijo en vez de un atributo porque es
  // markup —el SVG de la maqueta, puesto con `inline_svg_tag`— y un SVG entero dentro de un
  // atributo HTML es ilegible. El `<template>` no se renderiza ni ocupa sitio en la tarjeta.
  //
  // Sin ese hijo esto es cadena vacía, así que los tooltips que ya existían no cambian.
  get boxIcon() {
    const plantilla = this.querySelector('template[data-tooltip-icono]')

    return plantilla ? plantilla.innerHTML : ''
  }

  _getFlipOptions (selected) {
    let options = ['top', 'right', 'bottom', 'left']
    if (options.includes(selected)) {
      delete options[options.indexOf(selected)]
    }
    return options.flat()
  }

  // Con ratón: pasar por encima o enfocar. El clic se deja fuera en los operables porque, tras el hover,
  // tooltip.js lo toma por «cerrar»: pulsar un ⓘ sobre el que ya estás cerraría el globo que acabas de ver.
  _triggerMode () {
    const isMobile = /(Android|webOS|iPhone|iPad|iPod|BlackBerry|Windows Phone)/i.test(navigator.userAgent)
    if (isMobile) return 'click'

    return this.operable ? 'hover focus' : 'hover focus click'
  }
}

export default EmergiaTooltip
